import { useState } from "react";
import { Link } from "react-router-dom";
import { ChevronDown, ChevronUp } from "lucide-react";
import type { ColumnDef } from "@tanstack/react-table";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { DataTable } from "@/components/data-table/DataTable";
import { usePagedQuery } from "@/components/data-table/usePagedQuery";
import { formatDateBr } from "@/lib/format";
import {
  ACESSOS_KEY,
  USUARIOS_PATH,
  useConfiguracaoAcessos,
  type AcessoUsuario,
  type FiltroAcessoUsuario,
  type Origem,
  type Periodo,
  type StatusUsuario,
} from "./api";
import { PrimeiroAcesso } from "./AbaInstituicoes";
import { Campo, StatusBadge } from "./componentes";
import {
  FAIXA_VAZIA,
  ORIGEM_LABEL,
  SITUACAO_ADMIN_LABEL,
  SITUACOES_ADMIN,
  SITUACOES_PADRAO,
  STATUS_INFO,
  faixaParaFiltro,
  formatAcesso,
  formatDiasSemAcesso,
  formatNumero,
  rotuloStatus,
  type FaixaData,
  type ModoData,
} from "./formato";

const STATUS_USUARIO: StatusUsuario[] = ["OK", "ATENCAO", "INATIVO", "NUNCA"];
const DIAS_NOVOS = 10;

function colunas(mostrarInstituicao: boolean): ColumnDef<AcessoUsuario, unknown>[] {
  return [
    {
      accessorKey: "nome",
      header: "Usuário",
      cell: ({ row }) => (
        <div className="min-w-[10rem]">
          <div className="font-medium text-foreground">{row.original.nome}</div>
          <div className="text-xs text-muted-foreground">
            {[row.original.login, row.original.situacao === "I" ? "desativado" : undefined].filter(Boolean).join(" · ")}
          </div>
        </div>
      ),
    },
    ...(mostrarInstituicao
      ? [
          {
            accessorKey: "nomeInstituicao",
            header: "Instituição",
            cell: ({ row }) =>
              row.original.idInstituicao ? (
                <div className="min-w-[10rem]">
                  <Link to={`/Acessos/Instituicao/${row.original.idInstituicao}`} className="hover:underline">
                    {row.original.nomeInstituicao ?? `#${row.original.idInstituicao}`}
                  </Link>
                  <div className="text-xs text-muted-foreground">{SITUACAO_ADMIN_LABEL[row.original.situacaoInstituicao ?? ""]}</div>
                </div>
              ) : (
                "—"
              ),
          } satisfies ColumnDef<AcessoUsuario, unknown>,
        ]
      : []),
    {
      accessorKey: "status",
      header: "Status",
      cell: ({ row }) => (
        <div className="flex flex-col items-start gap-1">
          <StatusBadge status={row.original.status} tipo="usuario" />
          {row.original.reativado && <span className="text-xs text-emerald-700">reativado</span>}
        </div>
      ),
    },
    {
      accessorKey: "ultimoAcesso",
      header: "Último acesso",
      cell: ({ row }) => <span className="whitespace-nowrap">{formatAcesso(row.original.ultimoAcesso)}</span>,
    },
    {
      accessorKey: "diasSemAcesso",
      header: "Sem acesso",
      cell: ({ row }) => <span className="whitespace-nowrap">{formatDiasSemAcesso(row.original.diasSemAcesso)}</span>,
    },
    {
      accessorKey: "primeiroAcesso",
      header: "Primeiro acesso",
      cell: ({ row }) => <PrimeiroAcesso valor={row.original.primeiroAcesso} antesDoLog={row.original.primeiroAcessoAntesDoLog} />,
    },
    {
      accessorKey: "cadastro",
      header: "Cadastro",
      cell: ({ row }) => <span className="whitespace-nowrap">{formatDateBr(row.original.cadastro) || "—"}</span>,
    },
    { accessorKey: "logins", header: "Logins", cell: ({ row }) => <span className="tabular-nums">{formatNumero(row.original.logins)}</span> },
    { accessorKey: "diasAtivos", header: "Dias ativos", cell: ({ row }) => <span className="tabular-nums">{row.original.diasAtivos}</span> },
    {
      accessorKey: "loginsMobile",
      header: "Origem",
      cell: ({ row }) =>
        row.original.origemPredominante ? (
          <span title={`${row.original.loginsWeb} web · ${row.original.loginsMobile} app`}>{ORIGEM_LABEL[row.original.origemPredominante]}</span>
        ) : (
          "—"
        ),
    },
  ];
}

interface TabelaUsuariosProps {
  periodo: Periodo;
  /** Detalhe de uma instituição: lista só os usuários dela, sem a coluna/filtro de instituição. */
  idInstituicao?: number;
}

/** Usuários com primeiro/último acesso e filtros combináveis — global ou de uma instituição. */
export function TabelaUsuarios({ periodo, idInstituicao }: TabelaUsuariosProps) {
  const global = idInstituicao == null;
  const filtroPadrao: FiltroAcessoUsuario = global ? { situacaoUsuario: "A", situacoesInstituicao: SITUACOES_PADRAO } : { situacaoUsuario: "A" };
  const config = useConfiguracaoAcessos();
  const [primeiro, setPrimeiro] = useState<FaixaData>(FAIXA_VAZIA);
  const [ultimo, setUltimo] = useState<FaixaData>(FAIXA_VAZIA);
  const [maisFiltros, setMaisFiltros] = useState(false);

  const query = usePagedQuery<AcessoUsuario, FiltroAcessoUsuario>({
    key: [...ACESSOS_KEY, "usuarios"],
    path: USUARIOS_PATH,
    initialFiltro: filtroPadrao,
    extraFiltro: { ...periodo, idInstituicao },
  });

  const montar = (base: FiltroAcessoUsuario, p: FaixaData, u: FaixaData): FiltroAcessoUsuario => {
    const fp = faixaParaFiltro(p);
    const fu = faixaParaFiltro(u);
    return { ...base, primeiroAcessoDe: fp.de, primeiroAcessoAte: fp.ate, ultimoAcessoDe: fu.de, ultimoAcessoAte: fu.ate };
  };

  const buscar = () => query.applyFiltro(montar(query.draft, primeiro, ultimo));

  /** Atalhos: partem do filtro padrão, não acumulam com o que estava. */
  const atalho = (extra: FiltroAcessoUsuario, p: FaixaData = FAIXA_VAZIA) => {
    setPrimeiro(p);
    setUltimo(FAIXA_VAZIA);
    query.applyFiltro(montar({ ...filtroPadrao, ...extra }, p, FAIXA_VAZIA));
  };

  const diasInativo = config.data?.diasInativoUsuario;

  return (
    <DataTable
      query={query}
      columns={colunas(global)}
      getRowId={(r) => r.id}
      exportFilename={global ? "acessos-usuarios" : `acessos-usuarios-instituicao-${idInstituicao}`}
      emptyMessage="Nenhum usuário encontrado."
      toolbar={
        <div className="space-y-3">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-sm text-muted-foreground">Atalhos:</span>
            <Button size="sm" variant="outline" onClick={() => atalho({}, { modo: "ultimos", dias: DIAS_NOVOS })}>
              Novos (≤ {DIAS_NOVOS} dias)
            </Button>
            <Button size="sm" variant="outline" onClick={() => atalho({ status: ["INATIVO"] })}>
              Sumidos{diasInativo ? ` (≥ ${diasInativo} dias)` : ""}
            </Button>
            <Button size="sm" variant="outline" onClick={() => atalho({ status: ["NUNCA"] })}>
              Nunca acessaram
            </Button>
            <Button size="sm" variant="outline" onClick={() => atalho({ reativado: true })}>
              Reativados
            </Button>
            <Button size="sm" variant="ghost" onClick={() => atalho({})}>
              Limpar filtros
            </Button>
          </div>

          <div className="flex flex-wrap items-end gap-3">
            <Campo id="fu-busca" label="Nome / login" className="w-56">
              <Input
                id="fu-busca"
                value={query.draft.busca ?? ""}
                onChange={(e) => query.setDraft((f) => ({ ...f, busca: e.target.value }))}
                onKeyDown={(e) => e.key === "Enter" && buscar()}
              />
            </Campo>
            <Campo id="fu-status" label="Status de acesso" className="w-44">
              <Select
                value={query.draft.status?.[0] ?? "__all__"}
                onValueChange={(v) => query.setDraft((f) => ({ ...f, status: v === "__all__" ? undefined : [v as StatusUsuario] }))}
              >
                <SelectTrigger id="fu-status">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__all__">Todos</SelectItem>
                  {STATUS_USUARIO.map((s) => (
                    <SelectItem key={s} value={s}>
                      {s === "OK" ? rotuloStatus(s, "usuario") : STATUS_INFO[s].label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Campo>
            <FiltroFaixa id="fu-primeiro" label="Primeiro acesso" valor={primeiro} onChange={setPrimeiro} />
            <FiltroFaixa id="fu-ultimo" label="Último acesso" valor={ultimo} onChange={setUltimo} />
            <Button variant="ghost" onClick={() => setMaisFiltros((m) => !m)} aria-expanded={maisFiltros}>
              {maisFiltros ? <ChevronUp className="mr-1 h-4 w-4" /> : <ChevronDown className="mr-1 h-4 w-4" />}
              Mais filtros
            </Button>
            <Button onClick={buscar}>Buscar</Button>
          </div>

          {maisFiltros && (
            <div className="flex flex-wrap items-end gap-3">
              <Campo id="fu-situacao" label="Situação do usuário" className="w-40">
                <Select
                  value={query.draft.situacaoUsuario ?? "__all__"}
                  onValueChange={(v) => query.setDraft((f) => ({ ...f, situacaoUsuario: v === "__all__" ? undefined : v }))}
                >
                  <SelectTrigger id="fu-situacao">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="A">Ativos</SelectItem>
                    <SelectItem value="I">Desativados</SelectItem>
                    <SelectItem value="__all__">Todos</SelectItem>
                  </SelectContent>
                </Select>
              </Campo>
              {global && (
                <Campo id="fu-situacao-inst" label="Situação da instituição" className="w-44">
                  <Select
                    value={
                      !query.draft.situacoesInstituicao?.length
                        ? "todas"
                        : query.draft.situacoesInstituicao.length === 1
                          ? query.draft.situacoesInstituicao[0]!
                          : "padrao"
                    }
                    onValueChange={(v) =>
                      query.setDraft((f) => ({
                        ...f,
                        situacoesInstituicao: v === "padrao" ? SITUACOES_PADRAO : v === "todas" ? undefined : [v],
                      }))
                    }
                  >
                    <SelectTrigger id="fu-situacao-inst">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="padrao">Ativas + Trial</SelectItem>
                      {SITUACOES_ADMIN.map((s) => (
                        <SelectItem key={s.value} value={s.value}>
                          {s.label}
                        </SelectItem>
                      ))}
                      <SelectItem value="todas">Todas</SelectItem>
                    </SelectContent>
                  </Select>
                </Campo>
              )}
              <Campo id="fu-origem" label="Origem no período" className="w-36">
                <Select
                  value={query.draft.origem ?? "__all__"}
                  onValueChange={(v) => query.setDraft((f) => ({ ...f, origem: v === "__all__" ? undefined : (v as Origem) }))}
                >
                  <SelectTrigger id="fu-origem">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__all__">Qualquer</SelectItem>
                    {(Object.keys(ORIGEM_LABEL) as Origem[]).map((o) => (
                      <SelectItem key={o} value={o}>
                        Só {ORIGEM_LABEL[o]}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Campo>
              <Campo id="fu-min" label="Logins no período (mín.)" className="w-36">
                <Input
                  id="fu-min"
                  type="number"
                  min={0}
                  value={query.draft.loginsMin ?? ""}
                  onChange={(e) => query.setDraft((f) => ({ ...f, loginsMin: e.target.value === "" ? undefined : Number(e.target.value) }))}
                />
              </Campo>
              <Campo id="fu-max" label="Logins no período (máx.)" className="w-36">
                <Input
                  id="fu-max"
                  type="number"
                  min={0}
                  value={query.draft.loginsMax ?? ""}
                  onChange={(e) => query.setDraft((f) => ({ ...f, loginsMax: e.target.value === "" ? undefined : Number(e.target.value) }))}
                />
              </Campo>
              <Campo id="fu-cadastro" label="Cadastrado até" className="w-40">
                <Input
                  id="fu-cadastro"
                  type="date"
                  value={query.draft.cadastradoAte ?? ""}
                  onChange={(e) => query.setDraft((f) => ({ ...f, cadastradoAte: e.target.value || undefined }))}
                />
              </Campo>
            </div>
          )}
        </div>
      }
    />
  );
}

const MODOS: { value: ModoData; label: string }[] = [
  { value: "qualquer", label: "Qualquer data" },
  { value: "ultimos", label: "Nos últimos N dias" },
  { value: "antes", label: "Antes de" },
  { value: "depois", label: "Depois de" },
  { value: "entre", label: "Entre" },
];

function FiltroFaixa({ id, label, valor, onChange }: { id: string; label: string; valor: FaixaData; onChange: (f: FaixaData) => void }) {
  return (
    <Campo id={id} label={label}>
      <div className="flex items-center gap-2">
        <Select value={valor.modo} onValueChange={(m) => onChange({ ...FAIXA_VAZIA, modo: m as ModoData, dias: m === "ultimos" ? 10 : undefined })}>
          <SelectTrigger id={id} className="w-44">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {MODOS.map((m) => (
              <SelectItem key={m.value} value={m.value}>
                {m.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {valor.modo === "ultimos" && (
          <Input
            type="number"
            min={1}
            className="w-20"
            aria-label={`${label}: dias`}
            value={valor.dias ?? ""}
            onChange={(e) => onChange({ ...valor, dias: e.target.value === "" ? undefined : Number(e.target.value) })}
          />
        )}
        {(valor.modo === "depois" || valor.modo === "entre") && (
          <Input
            type="date"
            className="w-40"
            aria-label={`${label}: ${valor.modo === "entre" ? "de" : "depois de"}`}
            value={valor.de ?? ""}
            onChange={(e) => onChange({ ...valor, de: e.target.value || undefined })}
          />
        )}
        {(valor.modo === "antes" || valor.modo === "entre") && (
          <Input
            type="date"
            className="w-40"
            aria-label={`${label}: ${valor.modo === "entre" ? "até" : "antes de"}`}
            value={valor.ate ?? ""}
            onChange={(e) => onChange({ ...valor, ate: e.target.value || undefined })}
          />
        )}
      </div>
    </Campo>
  );
}
